import type { Farm, MetricEvent } from '../../../types/apiModels'
import { DCNamespaces } from '../namespaces'
import getUnusedPaddocks from '../../../services/farmMapping/getUnusedPaddocks'
import { getMetricEvents } from './getMetricEvents'

type PaddockRef = { id: string }

export type NamespaceValidationStatus = {
  disabled: boolean
  readOnly?: boolean
  totalError: number
  error: string | null
}

const USAGE_INCOMPLETE_MESSAGE =
  'Existen lotes sin uso. Se habilitará cuando cargue el uso a todos los lotes.'
const USAGE_FETCH_ERROR_MESSAGE =
  'No se pudo cargar la información del uso de lotes. Intente nuevamente.'

export async function unusedRequiredPaddocks(
  farm: Farm,
  year: string,
  namespace: string,
): Promise<NamespaceValidationStatus> {
  let unusedPaddocks: PaddockRef[] | null
  try {
    unusedPaddocks = await getUnusedPaddocks({
      farmId: farm.id,
      year,
      namespace: DCNamespaces.paddocks,
    })
  } catch (error) {
    return {
      disabled: true,
      readOnly: false,
      totalError: 0,
      error: USAGE_FETCH_ERROR_MESSAGE,
    }
  }

  if (!unusedPaddocks?.length) {
    return {
      disabled: false,
      readOnly: false,
      totalError: 0,
      error: null,
    }
  }

  let existingEvents: MetricEvent[] | null = []
  try {
    existingEvents = await getMetricEvents({ farmId: farm.id, namespace, year })
  } catch (error) {
    // Fail closed: if we cannot verify prior data, keep the tab disabled.
    return {
      disabled: true,
      readOnly: false,
      totalError: 0,
      error: USAGE_FETCH_ERROR_MESSAGE,
    }
  }

  if (existingEvents?.length) {
    return {
      disabled: false,
      readOnly: true,
      totalError: unusedPaddocks.length,
      error: USAGE_INCOMPLETE_MESSAGE,
    }
  }

  return {
    disabled: true,
    readOnly: false,
    totalError: 0,
    error: USAGE_INCOMPLETE_MESSAGE,
  }
}

export async function unusedWarningPaddocks(
  farm: Farm,
  year: string,
): Promise<NamespaceValidationStatus> {
  try {
    const unusedPaddocks = await getUnusedPaddocks({
      farmId: farm.id,
      year,
      namespace: DCNamespaces.paddocks,
    })
    const count = unusedPaddocks?.length ?? 0

    return {
      disabled: false,
      totalError: count,
      error: count
        ? 'Existen lotes sin uso. Se habilitará la carga de Pastoreo cuando cargue el uso a todos los lotes.'
        : null,
    }
  } catch (error) {
    return {
      disabled: false,
      totalError: 0,
      error: 'No se pudo cargar la información del uso de lotes. Intente nuevamente.',
    }
  }
}
